import { jwtDecode } from 'jwt-decode';
import Cookies, { CookieSetOptions } from 'universal-cookie';

import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';
import configuration from '@totem/utilities/configuration';
import logger from '@totem/utilities/logger';

import {
  getTokenExpirationDate as getTokenExpirationDateShared,
  getTokenExpirationDateWithFallback,
} from '../../../server/utilities/tokenUtilities';

const getCookies = () => new Cookies();

export interface AccountTokenPayload {
  id: string;
  authId: string;
  email: string;
  organizationId: string;
  role: number;
  refreshCount: number;
  exp: number;
  iss: string;
}

export const USER_ROLES = [
  {
    label: 'Tenant',
    value: -2,
  },
  {
    label: 'Vendor',
    value: -1,
  },
  {
    label: 'Collaborator',
    value: 0,
  },
  {
    label: 'Team Member',
    value: 1,
  },
  {
    label: 'Admin',
    value: 2,
  },
  {
    label: 'Systems Administrator',
    value: 3,
  },
];

export const getRoleString = (roleId) => {
  switch (roleId) {
    case -2:
      return 'Tenant';
    case -1:
      return 'Vendor';
    case 0:
      return 'Collaborator';
    case 1:
      return 'Team Member';
    case 2:
      return 'Admin';
    case 3:
      return 'Systems Administrator';
    default:
      return 'Collaborator';
  }
};

export const getRoleStyle = (roleId) => {
  switch (roleId) {
    case 0:
      return {
        fontWeight: 700,
        color: colors.neutral.dark,
      };
    case 1:
      return {
        fontWeight: 700,
        color: colors.brand.navy,
      };
    case 2:
      return {
        fontWeight: 700,
        color: colors.brand.green,
      };
    case 3:
      return {
        fontWeight: 700,
        color: colors.brand.red,
      };
    default:
      return {
        fontWeight: 700,
        color: colors.neutral.dark,
      };
  }
};

export const decodeToken = (token: string): AccountTokenPayload | null => {
  if (!token) {
    return null;
  }

  return jwtDecode<AccountTokenPayload>(token);
};

export const getTokenOrganizationId = (token: string): string | null => {
  const payload = decodeToken(token);
  return payload?.organizationId ?? null;
};

export const getTokenExpirationDate = (token: string): Date | null => {
  return getTokenExpirationDateShared(token);
};

export function getToken() {
  const accessToken = getCookies().get(
    `access_token_${configuration.ENVIRONMENT}`,
  );

  // TODO: Remove Debug Logging
  if (!configuration.isProd) {
    const orgId = getTokenOrganizationId(accessToken);
    logger.debug(`getToken: organizationId: ${orgId}`);
  }

  return accessToken;
}

export const getAccountPayloadSync = () => {
  return jwtDecode(getToken());
};

export const setTokenWithExpiration = (token: string, expiration: Date) => {
  const cookie: CookieSetOptions = {
    path: '/',
    sameSite: 'lax',
    httpOnly: false,
    expires: expiration,
  };
  if (!configuration.isLocal) {
    cookie.domain = '.ib-services.net';
  }

  getCookies().set(`access_token_${configuration.ENVIRONMENT}`, token, cookie);
};

export const setToken = (token: string, source: string) => {
  const { isValid, expiration } = getTokenExpirationDateWithFallback(token);

  if (isNotNull(isValid)) {
    logger.debug(
      `setToken from ${source}: Token IsValid: ${isValid.toString()}`,
    );
  } else {
    logger.debug('setToken from ${source}: Token IsValid: null');
  }
  if (isNotNull(expiration)) {
    logger.debug(
      `setToken from ${source}: Token Expiration: ${expiration.toLocaleString()}`,
    );
  } else {
    logger.debug('setToken from ${source}: Token Expiration: null');
  }

  if (!isValid || !expiration) {
    logger.warn('setToken from ${source}: invalid token, not setting cookie.');
    return;
  }

  const orgId = getTokenOrganizationId(token);
  logger.debug(`setToken from ${source}: organizationId: ${orgId}`);

  setTokenWithExpiration(token, expiration);
};

const clearToken = () => {
  const cookies = getCookies();
  const allCookies = cookies.getAll();
  Object.keys(allCookies).forEach((cookie) => cookies.remove(cookie));

  const cookieName = `access_token_${configuration.ENVIRONMENT}`;
  if (configuration.isLocal) {
    cookies.remove(cookieName, { path: '/' });
    return;
  }

  cookies.remove(cookieName, {
    path: '/',
    sameSite: 'lax',
    httpOnly: false,
  });

  cookies.remove(cookieName, {
    path: '/',
    domain: '.ib-services.net',
    sameSite: 'lax',
    httpOnly: false,
  });
};

export function logout() {
  clearToken();
}

const accountUtilities = {
  getRoleString,
  getRoleStyle,
  getAccountPayloadSync,
  getToken,
  getTokenExpirationDate,
  logout,
  setToken,
  clearToken,
  USER_ROLES,
};

export default accountUtilities;
